// Hand-off of get-started quiz answers into the signed-in experience.
//
// The quiz (web/src/pages/QuizFunnel.tsx) runs pre-auth; signing up remounts the
// whole app tree, so answers are passed through sessionStorage (same pattern the
// funnel already uses for its post-login redirect intent). On the authed side we
// SEED the health profile with the confidently-mappable fields and let Intake
// pre-fill the rest — the quiz never marks the profile "complete" or lets a user
// skip intake with a thinner background; it only avoids re-asking what they told us.

import { getHealthProfile, updateHealthProfile } from './healthProfile';
import type { DiagnosedCondition, CycleRegularity, UserHealthProfile } from '@researcher/shared';

const ANSWERS_KEY = 'quiz_answers_for_intake';

export type QuizAnswers = Record<string, string[]>;

export function stashQuizAnswers(answers: QuizAnswers): void {
  try {
    sessionStorage.setItem(ANSWERS_KEY, JSON.stringify(answers));
  } catch {
    /* sessionStorage unavailable (private mode / SSR) — handoff just no-ops */
  }
}

export function readQuizAnswers(): QuizAnswers | null {
  try {
    const raw = sessionStorage.getItem(ANSWERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as QuizAnswers) : null;
  } catch {
    return null;
  }
}

export function clearQuizAnswers(): void {
  try {
    sessionStorage.removeItem(ANSWERS_KEY);
  } catch {
    /* ignore */
  }
}

// ─── Quiz → profile field mapping (intentionally narrow) ────────────────
// Only the fields that map cleanly and losslessly. Age stays out (the quiz is
// banded; intake captures exact DOB). Goals stay out (different taxonomy +
// they create persisted goal records). Repro status stays out (quiz never asks).

/** Quiz diagnosis labels → the canonical condition labels used in the profile/intake. */
const DIAGNOSIS_LABEL_MAP: Record<string, string> = {
  'Thyroid disorder': 'Thyroid condition',
};

/** Condition labels the profile/intake recognize (mirrors Intake's CONDITION_OPTIONS). */
const KNOWN_CONDITIONS = new Set([
  'Endometriosis', 'PCOS', 'PMDD', 'Fibroids', 'Adenomyosis',
  'Thyroid condition', 'Interstitial cystitis', 'Perimenopause',
]);

/** Diagnosed-condition names from the quiz `diagnoses` answer (normalized, deduped, known-only). */
export function quizDiagnosedConditions(answers: QuizAnswers): string[] {
  const raw = answers.diagnoses || [];
  const out: string[] = [];
  for (const d of raw) {
    if (d === 'None of these') continue;
    const name = DIAGNOSIS_LABEL_MAP[d] || d;
    if (KNOWN_CONDITIONS.has(name) && !out.includes(name)) out.push(name);
  }
  return out;
}

/** Cycle regularity from the quiz `periods` answer, when it maps cleanly. */
export function quizCycleRegularity(answers: QuizAnswers): CycleRegularity | null {
  switch (answers.periods?.[0]) {
    case 'Regular and manageable': return 'regular';
    case 'Irregular or unpredictable': return 'somewhat_irregular';
    // "Heavy or painful" / "Absent or very light" say nothing about regularity.
    default: return null;
  }
}

/**
 * Seed the profile from quiz answers WITHOUT clobbering anything the user already
 * has, and without marking cycle setup / onboarding complete. Best-effort.
 */
export async function seedProfileFromQuiz(answers: QuizAnswers): Promise<void> {
  let existing: UserHealthProfile | null = null;
  try {
    existing = (await getHealthProfile()).healthProfile;
  } catch {
    /* no profile yet / fetch failed — treat as empty */
  }

  const patch: Partial<{ conditions: DiagnosedCondition[]; cycleRegularity: CycleRegularity }> = {};

  // Only seed conditions when the user has none — never overwrite real data.
  if (!existing?.conditions?.length) {
    const names = quizDiagnosedConditions(answers);
    if (names.length) {
      patch.conditions = names.map((name) => ({ name, status: 'active' as const }));
    }
  }

  if (!existing?.cycleRegularity) {
    const reg = quizCycleRegularity(answers);
    if (reg) patch.cycleRegularity = reg;
  }

  if (Object.keys(patch).length) {
    await updateHealthProfile(patch);
  }
}
